import React, { useEffect, useState, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { exists, getDebugFn, redirectWindowLocation, Analytics } from "utils";
import { getFlowDestination } from "containers/App/AppAuth";
import { FLOW_STATE } from "components/Paypal";
import { FullPageSpinner } from "components/Spinner";
import * as accountsCustomerApi from "api/accounts-customer";
import { appendStateToRedirectUrl, getPaypalToken, getRedirectState } from "./common";
import { isRedirectURLAllowed, RedirectNotAllowedError } from "utils/redirect";

export const PaypalCancel = () => {
  const analyticsInstance = Analytics();
  const location = useLocation();
  const history = useHistory();
  const sessionPayload = useRef(null); // hold on to session payload after loading so we can use to assign logs to the user

  const paypalToken = getPaypalToken(location);
  const redirectToken = getRedirectState(location);

  const [redirect, setRedirect] = useState(false);
  useEffect(() => {
    const logFields = {};
    const processPaypalCancel = async () => {
      try {
        logFields.paypalToken = paypalToken;

        logFields.location = "verifyToken";
        const verifyResponse = await accountsCustomerApi.verifyToken(redirectToken);
        const session = verifyResponse.data;
        sessionPayload.current = session; // hold session so we can use with logging later
        const flowConfig = session?.sessionCtx?.flowConfig || {};
        const postOAuthURLOverride = flowConfig.postOAuthURLOverride || flowConfig.connectPaypalCompleteUrlOverride; // TODO: may not have to support connectPaypalCompleteUrlOverride if applovin isn't using yet.
        const debug = getDebugFn(session?.sessionCtx);
        const intCtx = session?.sessionCtx?.intCtx;
        debug("intCtx", intCtx);

        if (intCtx) {
          // widget / iframe
          /**
           * See code comments in this section of './Success.jsx' for more info.
          */
          const finalUrl = appendStateToRedirectUrl((postOAuthURLOverride || intCtx), FLOW_STATE.CANCEL);
          debug("finalUrl", finalUrl);

          // Validate redirect URL against the integrator's allowed patterns, which
          // were signed into the redirect token at flow start (SettingsProvider is
          // not populated on this callback route, but the verified token payload is
          // trustworthy because it is server-signed).
          const redirectAllowed = isRedirectURLAllowed(finalUrl, {
            allowed_domain_redirect_patterns: session?.allowedDomainRedirectPatterns,
          });
          if (!redirectAllowed) {
            const error = new RedirectNotAllowedError();
            logFields.redirectUrl = finalUrl;
            logFields.error = error.message;
            analyticsInstance.track({
              title: "[ANALYTICS]: TRACK",
              eventStr: "PayPal_Redirect_Blocked",
              payload: {
                redirectUrl: finalUrl,
                flow: session?.sessionCtx?.flow,
              },
              jwt: sessionPayload.current?.sessionJWT,
            });
            throw error;
          }

          analyticsInstance.track({
            title: "[ANALYTICS]: TRACK",
            eventStr: "PayPal_Process_OAuth_Cancel_Return",
            payload: {
              isSuccess: true,
              isIFrame: true,
              redirectUrl: finalUrl,
              flow: session?.sessionCtx?.flow,
            },
            jwt: sessionPayload.current?.sessionJWT,
          });
          await new Promise((resolve) => setTimeout(resolve, 100)); // allow analytics to flush
          setRedirect(true);
          redirectWindowLocation(finalUrl);
        } else {
          // fullpage
          const flow = session?.sessionCtx?.flow;
          const finalUrl = getFlowDestination(flow);
          debug("finalUrl", finalUrl);
          analyticsInstance.track({
            title: "[ANALYTICS]: TRACK",
            eventStr: "PayPal_Process_OAuth_Cancel_Return",
            payload: {
              isSuccess: true,
              isIFrame: false,
              redirectUrl: finalUrl,
              flow: session?.sessionCtx?.flow,
            },
            jwt: sessionPayload.current?.sessionJWT,
          });
          await new Promise((resolve) => setTimeout(resolve, 100)); // allow analytics to flush
          setRedirect(true);
          history.replace(finalUrl);
        }
      } catch (error) {
        accountsCustomerApi.logError(sessionPayload.current?.sessionJWT || null, logFields);
        // depending on where this fails, the sessionPayload may not be set 
        analyticsInstance.track({
          title: "[ANALYTICS]: TRACK",
          eventStr: "PayPal_Process_OAuth_Cancel_Return",
          payload: {
            isSuccess: false,
            flow: sessionPayload.current?.sessionCtx?.flow,
            error: error?.message || "unable to parse error message",
          },
          jwt: sessionPayload.current?.sessionJWT,
        });
        // push error up the global application boundary
        setRedirect(() => {
          throw error;
        });
      }
    };
    if (exists(redirectToken) && exists(paypalToken)) {
      processPaypalCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToken, paypalToken, history]);

  if (redirect) {
    return <FullPageSpinner data-testid="paypal-cancel-redirect" />;
  }
  return <FullPageSpinner data-testid="paypal-cancel" />;
};
